import {
	AddCircleOutline,
	Create,
	Delete,
	KeyboardArrowLeft,
	KeyboardArrowRight,
	KeyboardDoubleArrowLeft,
	KeyboardDoubleArrowRight,
	Lock,
	PlaylistAdd,
	Restore,
	Update,
	ZoomIn,
} from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Checkbox,
	Divider,
	FormControl,
	FormControlLabel,
	Grid2,
	IconButton,
	InputAdornment,
	InputLabel,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import { EChartsInstance } from "echarts-for-react";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { ConfirmCancelActionsV2 } from "../components/modals/ConfirmCancelActionsV2";
import { ManageTagsModalV2 } from "../components/modals/ManageTagsModalV2";
import { SaveUpdateDeleteViewModalV2 } from "../components/modals/SaveUpdateDeleteViewModalV2";
import { useTrendingContextV2 } from "../context/TrendingContextV2";
import { MIN_ONLINE_INTERVAL_SECONDS } from "../models/TrendingModelsV2";
import type {
	CustomOptions,
	View,
	scopeType,
} from "../models/TrendingModelsV2";
import { filterViewsByWords } from "../utils/filterViewsByWords";
import { hasUnsavedViewChanges } from "../utils/hasUnsavedViewChanges";
import { calculateCounterpartDate } from "../utils/calculateDateFromPeriod";
import { dateNavigator } from "../utils/dateNavigator";
import {
	TIME_ZONE_MODES,
	TIME_ZONE_SHORT_LABELS,
	type TimeZoneMode,
	getServerTimeZone,
	getTimeZoneLabel,
	toPickerTimeZone,
} from "../utils/timeZone";
import type { AggregationMode } from "../utils/trendingUrlState";

const HEADER_FONT_SIZE = "13px";
const HEADER_MENU_PROPS = {
	sx: {
		"& .MuiMenuItem-root": { fontSize: HEADER_FONT_SIZE },
		"& .MuiMenuItem-root .MuiListItemText-primary": {
			fontSize: HEADER_FONT_SIZE,
		},
	},
};
const VIEW_NAME_MAX_ROWS = 3;
const VIEW_SELECTOR_HEIGHT = 40;

const AGGREGATION_LABELS: Record<AggregationMode, string> = {
	avg: "Avg",
	last: "Last",
	first: "First",
	min: "Min",
	max: "Max",
	envelope: "Envelope",
};

interface HeaderSectionProps {
	autoRefresh: boolean;
	setAutoRefresh: (value: boolean) => void;
	customOptions: CustomOptions;
	setCustomOptions: React.Dispatch<React.SetStateAction<CustomOptions>>;
	aggregationMode: AggregationMode;
	setAggregationMode: (mode: AggregationMode) => void;
	chartInstance: EChartsInstance | null;
}

export const HeaderSectionV2: React.FC<HeaderSectionProps> = React.memo(
	({
		autoRefresh,
		setAutoRefresh,
		customOptions,
		setCustomOptions,
		aggregationMode,
		setAggregationMode,
		chartInstance,
	}) => {
		const queryClient = useQueryClient();
		const {
			state: {
				timeScopeStart,
				timeScopeEnd,
				scope,
				viewSelected,
				views,
				viewTags,
			},
			actions: { setTotalScope, setViewSelected, setViewTagsAndRefetch },
		} = useTrendingContextV2();

		// View management state
		const [viewModalMode, setViewModalMode] = useState<
			"create" | "update" | "delete" | null
		>(null);
		const [viewForModal, setViewForModal] = useState<View | null>(null);
		const [manageTagsOpen, setManageTagsOpen] = useState(false);
		const [discardChangesOpen, setDiscardChangesOpen] = useState(false);
		const [viewToLoad, setViewToLoad] = useState<View | null>(null);
		const [error, setError] = useState("");
		const [areaZoomActive, setAreaZoomActive] = useState(false);
		const [onlineIntervalText, setOnlineIntervalText] = useState(
			String(customOptions.onlineIntervalSeconds),
		);
		const onlineIntervalRef = useRef<HTMLInputElement>(null);
		const [timeZoneMenuOpen, setTimeZoneMenuOpen] = useState(false);
		const [timeZoneTooltipOpen, setTimeZoneTooltipOpen] = useState(false);

		const handleDateNavigator = (
			operator: "add" | "subtract",
			current = false,
		) => {
			const newDates = dateNavigator(
				timeScopeStart,
				timeScopeEnd,
				scope,
				operator,
				current,
			);
			if (newDates) {
				setTotalScope({
					start: newDates.newStartDate,
					end: newDates.newEndDate,
					scope: scope,
				});
			}
		};

		const handlePartialDateNavigator = (operator: "add" | "subtract") => {
			// Calculate 20% of the current period
			const durationInMs = moment(timeScopeEnd).diff(moment(timeScopeStart));
			const partialDuration = Math.round(durationInMs * 0.2);

			const newStartDate = moment(timeScopeStart)
				[operator](partialDuration, "milliseconds")
				.toDate();
			const newEndDate = moment(timeScopeEnd)
				[operator](partialDuration, "milliseconds")
				.toDate();

			setTotalScope({
				start: newStartDate,
				end: newEndDate,
				scope: scope,
			});
		};

		const handleDateChange = (newValue: any, key: "start" | "end") => {
			const newDate = moment(newValue).toDate();

			// If scope is "custom", just update the changed date
			if (scope === "custom") {
				setTotalScope({ [key]: newDate });
				return;
			}

			// Calculate the counterpart date based on the period
			const calculatedDates = calculateCounterpartDate(newDate, key, scope);

			if (calculatedDates) {
				// Update both start and end to maintain the period duration
				setTotalScope({
					start: calculatedDates.start,
					end: calculatedDates.end,
					scope: scope, // Keep the same scope
				});
			} else {
				// Fallback: just update the single date
				setTotalScope({ [key]: newDate });
			}
		};

		const handleChangeScope = (value: scopeType) => {
			if (value === "custom") {
				// Just update scope to custom, keep current dates
				setTotalScope({
					scope: value,
				});
				return;
			}

			// Keep the current START date and calculate END based on new period
			const calculatedDates = calculateCounterpartDate(
				timeScopeStart,
				"start",
				value,
			);

			if (calculatedDates) {
				setTotalScope({
					start: calculatedDates.start, // Keep the same start
					end: calculatedDates.end, // Calculate new end
					scope: value,
				});
			}
		};

		// View management handlers
		const selectView = async (view: View) => {
			setViewSelected(view);
			// Invalidate the viewTags query to trigger a refetch with the new viewId
			await queryClient.invalidateQueries({
				queryKey: ["viewTags-v2", view.ViewId],
			});
		};

		const handleViewChange = async (
			event: React.ChangeEvent<{}>,
			value: View | null,
		) => {
			if (!value || value.ViewId === viewSelected?.ViewId) return;

			if (hasUnsavedViewChanges(viewTags, viewSelected)) {
				setViewToLoad(value);
				setDiscardChangesOpen(true);
				return;
			}

			await selectView(value);
		};

		const setOnlineInterval = (seconds: number) => {
			setCustomOptions((prevOptions) => ({
				...prevOptions,
				onlineIntervalSeconds: Math.max(MIN_ONLINE_INTERVAL_SECONDS, seconds),
			}));
		};

		const handleOnlineIntervalChange = (
			event: React.ChangeEvent<HTMLInputElement>,
		) => {
			const typed = event.target.value;
			setOnlineIntervalText(typed);

			const seconds = Number.parseInt(typed, 10);
			if (Number.isNaN(seconds) || seconds < MIN_ONLINE_INTERVAL_SECONDS) return;

			setOnlineInterval(seconds);
		};

		const handleOnlineIntervalBlur = () => {
			const seconds = Number.parseInt(onlineIntervalText, 10);

			if (Number.isNaN(seconds) || seconds < MIN_ONLINE_INTERVAL_SECONDS) {
				setOnlineInterval(MIN_ONLINE_INTERVAL_SECONDS);
				setOnlineIntervalText(String(MIN_ONLINE_INTERVAL_SECONDS));
				return;
			}

			setOnlineIntervalText(String(seconds));
		};

		useEffect(() => {
			setOnlineIntervalText(String(customOptions.onlineIntervalSeconds));
		}, [customOptions.onlineIntervalSeconds]);

		// The wheel event has to be native so it can be non passive and stop the scroll
		useEffect(() => {
			const input = onlineIntervalRef.current;
			if (!input || !autoRefresh) return;

			const handleWheel = (event: WheelEvent) => {
				event.preventDefault();

				setCustomOptions((prevOptions) => ({
					...prevOptions,
					onlineIntervalSeconds: Math.max(
						MIN_ONLINE_INTERVAL_SECONDS,
						prevOptions.onlineIntervalSeconds + (event.deltaY < 0 ? 1 : -1),
					),
				}));
			};

			input.addEventListener("wheel", handleWheel, { passive: false });
			return () => input.removeEventListener("wheel", handleWheel);
		}, [setCustomOptions]);

		const openNewView = () => {
			setViewSelected(null);
			setViewTagsAndRefetch({});
			setManageTagsOpen(true);
		};

		const handleOpenNewView = () => {
			if (hasUnsavedViewChanges(viewTags, viewSelected)) {
				setViewToLoad(null);
				setDiscardChangesOpen(true);
				return;
			}

			openNewView();
		};

		const handleCloseDiscardChanges = () => {
			setDiscardChangesOpen(false);
			setViewToLoad(null);
		};

		const handleDiscardChanges = async () => {
			handleCloseDiscardChanges();

			if (viewToLoad) {
				await selectView(viewToLoad);
				return;
			}

			openNewView();
		};

		const handleDeleteView = (view: View, e: React.MouseEvent) => {
			e.stopPropagation();
			setViewForModal(view);
			setViewModalMode("delete");
		};

		const handleEditView = (view: View, e: React.MouseEvent) => {
			e.stopPropagation();
			setViewForModal(view);
			setViewModalMode("update");
		};

		// Zoom control handlers
		const dispatchAreaZoom = (active: boolean) => {
			if (!chartInstance || (chartInstance as any)._disposed) return false;

			chartInstance.dispatchAction({
				type: "takeGlobalCursor",
				key: "dataZoomSelect",
				dataZoomSelectActive: active,
			});
			return true;
		};

		const handleToggleAreaZoom = () => {
			const nextActive = !areaZoomActive;
			if (!dispatchAreaZoom(nextActive)) return;

			setAreaZoomActive(nextActive);
		};

		const handleRestoreZoom = () => {
			if (!chartInstance || (chartInstance as any)._disposed) return;

			dispatchAreaZoom(false);
			setAreaZoomActive(false);

			chartInstance.dispatchAction({
				type: "dataZoom",
				start: 0,
				end: 100,
			});
		};

		useEffect(() => {
			if (!areaZoomActive) return;

			dispatchAreaZoom(true);
		}, [chartInstance, areaZoomActive]);

		return (
			<>
				<Grid2
					size={12}
					container
					spacing={1}
					justifyContent="space-between"
					alignItems="stretch"
					style={{ height: "auto" }}
					sx={{
						"& .MuiInputBase-input": { fontSize: HEADER_FONT_SIZE },
						"& .MuiInputLabel-root": { fontSize: HEADER_FONT_SIZE },
						"& .MuiFormControlLabel-label": { fontSize: HEADER_FONT_SIZE },
						"& .MuiInputAdornment-root": { fontSize: HEADER_FONT_SIZE },
					}}
				>
					{/* Left side: Date pickers and navigation */}
					<Grid2
						size={{ md: 5, sm: 12, xs: 12 }}
						container
						justifyContent="flex-start"
						alignItems="stretch"
						wrap="wrap"
					>
						<Grid2
							container
							spacing={2}
							component={Paper}
							elevation={0}
							style={{
								backgroundColor: "white",
								display: "flex",
								flexWrap: "wrap",
								gap: "4px",
								alignItems: "center",
								alignContent: "center",
								width: "100%",
								height: "100%",
							}}
						>
							<Grid2>
								<Tooltip title="Previous period" arrow>
									<IconButton
										onClick={() => handleDateNavigator("subtract")}
										size="small"
									>
										<KeyboardDoubleArrowLeft
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>

							<Grid2>
								<Tooltip title="Pan backward 20%" arrow>
									<IconButton
										onClick={() => handlePartialDateNavigator("subtract")}
										size="small"
									>
										<KeyboardArrowLeft
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>

							<Grid2 size={{ md: 3 }}>
								<LocalizationProvider dateAdapter={AdapterMoment}>
									<DateTimePicker
										label="Start"
										format="MM/DD/YYYY HH:mm:ss"
										timezone={toPickerTimeZone(customOptions.timeZoneMode)}
										value={moment(timeScopeStart)}
										onChange={(newValue) => {
											if (newValue) {
												handleDateChange(newValue.toDate(), "start");
											}
										}}
										minutesStep={1}
										slotProps={{
											textField: {
												size: "small",
												variant: "outlined",
												fullWidth: true,
												sx: {
													"& .MuiInputBase-input": {
													fontSize: HEADER_FONT_SIZE,
												},
												},
											},
										}}
									/>
								</LocalizationProvider>
							</Grid2>

							<Grid2 size={{ md: 1.75 }} marginTop={0.5}>
								<FormControl variant="outlined" fullWidth size="small">
									<Select
										value={scope}
										onChange={(e) =>
											handleChangeScope(e.target.value as scopeType)
										}
										MenuProps={HEADER_MENU_PROPS}
										displayEmpty
									>
										<MenuItem value="1 min">1 min</MenuItem>
										<MenuItem value="10 min">10 min</MenuItem>
										<MenuItem value="30 min">30 min</MenuItem>
										<MenuItem value="1 hour">1 hour</MenuItem>
										<MenuItem value="4 hours">4 hours</MenuItem>
										<MenuItem value="12 hours">12 hours</MenuItem>
										<MenuItem value="1 day">1 day</MenuItem>
										<MenuItem value="3 days">3 days</MenuItem>
										<MenuItem value="10 days">10 days</MenuItem>
										<MenuItem value="custom">Custom</MenuItem>
									</Select>
								</FormControl>
							</Grid2>

							<Grid2 size={{ md: 3 }}>
								<LocalizationProvider dateAdapter={AdapterMoment}>
									<DateTimePicker
										label="End"
										format="MM/DD/YYYY HH:mm:ss"
										timezone={toPickerTimeZone(customOptions.timeZoneMode)}
										value={autoRefresh ? null : moment(timeScopeEnd)}
										onChange={(newValue) => {
											if (newValue) {
												handleDateChange(newValue.toDate(), "end");
											}
										}}
										minutesStep={1}
										slotProps={{
											textField: {
												size: "small",
												variant: "outlined",
												fullWidth: true,
												sx: {
													"& .MuiInputBase-input": {
													fontSize: HEADER_FONT_SIZE,
												},
												},
											},
										}}
										disabled={autoRefresh}
									/>
								</LocalizationProvider>
							</Grid2>

							<Grid2 size={{ md: 1.75 }} marginTop={0.5}>
								<Tooltip
									title={`Timezone used to display dates. Server: ${getServerTimeZone()}`}
									arrow
									open={timeZoneTooltipOpen && !timeZoneMenuOpen}
									onOpen={() => setTimeZoneTooltipOpen(true)}
									onClose={() => setTimeZoneTooltipOpen(false)}
								>
									<FormControl variant="outlined" fullWidth size="small">
										<InputLabel id="trending-timezone-label">
											Timezone
										</InputLabel>
										<Select
											labelId="trending-timezone-label"
											id="trending-timezone"
											label="Timezone"
											open={timeZoneMenuOpen}
											onOpen={() => setTimeZoneMenuOpen(true)}
											onClose={() => setTimeZoneMenuOpen(false)}
											value={customOptions.timeZoneMode}
											onChange={(e) =>
												setCustomOptions((prev) => ({
													...prev,
													timeZoneMode: e.target.value as TimeZoneMode,
												}))
											}
											renderValue={(value) =>
												TIME_ZONE_SHORT_LABELS[value as TimeZoneMode]
											}
											MenuProps={HEADER_MENU_PROPS}
										>
											{TIME_ZONE_MODES.map((mode) => (
												<MenuItem key={mode} value={mode}>
													{getTimeZoneLabel(mode)}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Tooltip>
							</Grid2>

							<Grid2>
								<Tooltip title="Pan forward 20%" arrow>
									<IconButton
										onClick={() => handlePartialDateNavigator("add")}
										size="small"
									>
										<KeyboardArrowRight
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>
							<Grid2>
								<Tooltip title="Next period" arrow>
									<IconButton
										onClick={() => handleDateNavigator("add")}
										size="small"
									>
										<KeyboardDoubleArrowRight
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>

							<Grid2>
								<Tooltip title="Jump to current time" arrow>
									<IconButton
										onClick={() => handleDateNavigator("subtract", true)}
										size="small"
									>
										<Update fontSize="medium" sx={{ color: "black" }} />
									</IconButton>
								</Tooltip>
							</Grid2>
						</Grid2>
					</Grid2>

					{/* Chart Options */}
					<Grid2 size={{ md: 5, sm: 6, xs: 12 }}>
						<Paper
							elevation={0}
							sx={{
								p: 1,
								overflow: "auto",
								height: "100%",
								display: "flex",
								alignItems: "center",
							}}
						>
							{/* Container with chart options dropdown, divider, and toggles */}
							<div
								style={{
									display: "flex",
									gap: "8px",
									width: "100%",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								{/* Left side - Multiple Select Dropdown */}
								<Box sx={{ width: 155 }}>
									<FormControl fullWidth size="small">
										<Select
											multiple
											value={[
												...(customOptions.combinedView ? ["combined"] : []),
												...(customOptions.showGridLines ? ["grid"] : []),
												...(customOptions.smoothLines ? ["smooth"] : []),
												...(customOptions.steppedLines ? ["stepped"] : []),
												...(customOptions.showTooltip ? ["tooltip"] : []),
												...(customOptions.showToolbox ? ["toolbox"] : []),
												...(customOptions.showYAxisNames ? ["yAxisNames"] : []),
											]}
											onChange={(e) => {
												const selected = e.target.value as string[];
												setCustomOptions((prevOptions) => ({
													...prevOptions,
													combinedView: selected.includes("combined"),
													showGridLines: selected.includes("grid"),
													smoothLines:
														selected.includes("smooth") &&
														!selected.includes("stepped"),
													steppedLines: selected.includes("stepped"),
													showTooltip: selected.includes("tooltip"),
													showToolbox: selected.includes("toolbox"),
													showYAxisNames: selected.includes("yAxisNames"),
												}));
											}}
											renderValue={(selected) => (
												<span style={{ fontSize: HEADER_FONT_SIZE }}>
													Chart: {(selected as string[]).length} options
												</span>
											)}
											MenuProps={HEADER_MENU_PROPS}
										>
											<MenuItem value="combined" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.combinedView}
													size="small"
												/>
												<ListItemText>Combined</ListItemText>
											</MenuItem>
											<MenuItem value="grid" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showGridLines}
													size="small"
												/>
												<ListItemText>Grid</ListItemText>
											</MenuItem>
											<MenuItem value="smooth" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.smoothLines}
													size="small"
												/>
												<ListItemText>Smooth</ListItemText>
											</MenuItem>
											<MenuItem value="stepped" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.steppedLines}
													size="small"
												/>
												<ListItemText>Stepped</ListItemText>
											</MenuItem>
											<MenuItem value="tooltip" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showTooltip}
													size="small"
												/>
												<ListItemText>Tooltip</ListItemText>
											</MenuItem>
											<MenuItem value="toolbox" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showToolbox}
													size="small"
												/>
												<ListItemText>Toolbox</ListItemText>
											</MenuItem>
											<MenuItem value="yAxisNames" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showYAxisNames}
													size="small"
												/>
												<ListItemText>Y-Axis Names</ListItemText>
											</MenuItem>
										</Select>
									</FormControl>
								</Box>

								<Box sx={{ minWidth: 150 }}>
									<FormControl fullWidth size="small">
										<Select
											value={aggregationMode}
											onChange={(e) => {
												setAggregationMode(e.target.value as AggregationMode);
											}}
											renderValue={(selected) =>
												`Agg: ${AGGREGATION_LABELS[selected as AggregationMode]}`
											}
											MenuProps={HEADER_MENU_PROPS}
										>
											<MenuItem value="avg">Average</MenuItem>
											<MenuItem value="last">Last value</MenuItem>
											<MenuItem value="first">First value</MenuItem>
											<MenuItem value="min">Min value</MenuItem>
											<MenuItem value="max">Max value</MenuItem>
											<MenuItem value="envelope">Envelope</MenuItem>
										</Select>
									</FormControl>
								</Box>

								{/* Vertical Divider */}
								<Divider orientation="vertical" flexItem />

								{/* Online Toggle */}
								<FormControlLabel
									checked={autoRefresh}
									control={<Switch color="primary" size="small" />}
									label={
										<span style={{ fontSize: HEADER_FONT_SIZE, fontWeight: 600 }}>
											Online
										</span>
									}
									style={{ margin: "0" }}
									onChange={(e, checked) => {
										if (checked) {
											// When enabling auto-refresh, set end to now
											// and keep the current period to calculate start
											const now = new Date();
											if (scope !== "custom") {
												const calculatedDates = calculateCounterpartDate(
													now,
													"end",
													scope,
												);
												if (calculatedDates) {
													setTotalScope({
														start: calculatedDates.start,
														end: calculatedDates.end,
														scope: scope,
													});
												}
											}
										}
										setAutoRefresh(checked);
									}}
								/>

								<Tooltip title="Online interval in seconds" arrow>
									<TextField
										inputRef={onlineIntervalRef}
										type="number"
										size="small"
										disabled={!autoRefresh}
										value={onlineIntervalText}
										onChange={handleOnlineIntervalChange}
										onBlur={handleOnlineIntervalBlur}
										sx={{
											width: 96,
											"& .MuiInputBase-input": { fontSize: HEADER_FONT_SIZE },
										}}
										slotProps={{
											htmlInput: {
												min: MIN_ONLINE_INTERVAL_SECONDS,
												step: 1,
											},
											input: {
												endAdornment: (
													<InputAdornment position="end">
														<span style={{ fontSize: HEADER_FONT_SIZE }}>s</span>
													</InputAdornment>
												),
											},
										}}
									/>
								</Tooltip>

								{/* Second Vertical Divider */}
								<Divider orientation="vertical" flexItem />

								<Tooltip
									title={areaZoomActive ? "Exit area zoom" : "Area zoom"}
									arrow
								>
									<IconButton
										color="primary"
										onClick={handleToggleAreaZoom}
										size="small"
										sx={{
											borderRadius: "4px",
											border: "1px solid",
											borderColor: areaZoomActive
												? "primary.main"
												: "transparent",
											backgroundColor: areaZoomActive
												? "primary.main"
												: "transparent",
											color: areaZoomActive
												? "primary.contrastText"
												: "primary.main",
											"&:hover": {
												backgroundColor: areaZoomActive
													? "primary.dark"
													: undefined,
											},
										}}
									>
										<ZoomIn fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Restore zoom" arrow>
									<IconButton
										color="primary"
										onClick={handleRestoreZoom}
										size="small"
									>
										<Restore fontSize="small" />
									</IconButton>
								</Tooltip>
							</div>
						</Paper>
					</Grid2>

					{/* View Selector - Inline Autocomplete */}
					<Grid2
						size={{ md: 2, sm: 6, xs: 12 }}
						container
						justifyContent="flex-end"
						alignItems="stretch"
					>
						<Paper
							elevation={0}
							sx={{
								p: 1,
								backgroundColor: "white",
								width: "100%",
								height: "100%",
								display: "flex",
								alignItems: "center",
								gap: "2px",
							}}
						>
							<Tooltip title="Manage tags" arrow>
								<span>
									<IconButton
										size="small"
										color="primary"
										disabled={
											!viewSelected && Object.keys(viewTags).length === 0
										}
										onClick={() => setManageTagsOpen(true)}
									>
										<PlaylistAdd fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>

							<Tooltip title="New view" arrow>
								<IconButton
									size="small"
									color="primary"
									onClick={handleOpenNewView}
								>
									<AddCircleOutline fontSize="small" />
								</IconButton>
							</Tooltip>

							{/* Autocomplete View Selector */}
							<Autocomplete
								fullWidth
								sx={{
									flex: 1,
									minWidth: 0,
									"& .MuiAutocomplete-clearIndicator": { display: "none" },
								}}
								size="small"
								id="view-selector"
								clearIcon={null}
								options={views}
								filterOptions={filterViewsByWords}
								getOptionKey={(option) => option.ViewId}
								getOptionLabel={(option) => option.ViewName}
								value={viewSelected}
								isOptionEqualToValue={(option, value) =>
									option.ViewId === value?.ViewId
								}
								onChange={handleViewChange}
								noOptionsText="No views available"
								slotProps={{
									paper: {
										sx: {
											"& .MuiAutocomplete-option": {
												fontSize: HEADER_FONT_SIZE,
											},
											"& .MuiAutocomplete-noOptions": {
												fontSize: HEADER_FONT_SIZE,
											},
										},
									},
								}}
								renderOption={(props, option) => (
									<Box
										{...props}
										component="li"
										style={{
											display: "flex",
											justifyContent: "space-between",
											width: "100%",
										}}
									>
										<div
											style={{
												flex: 1,
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											{option.ViewName}
										</div>
										<div
											style={{
												display: "flex",
												gap: "2px",
											}}
										>
											{!option.IsPublic && (
												<Tooltip title="Private" arrow>
													<Lock
														fontSize="small"
														style={{
															height: "16px",
															width: "16px",
															color: "rgba(0, 0, 0, 0.54)",
														}}
													/>
												</Tooltip>
											)}
											<IconButton
												size="small"
												onClick={(e) => handleEditView(option, e)}
											>
												<Create
													fontSize="small"
													color="primary"
													style={{ height: "16px", width: "16px" }}
												/>
											</IconButton>
											<IconButton
												size="small"
												onClick={(e) => handleDeleteView(option, e)}
											>
												<Delete
													fontSize="small"
													color="secondary"
													style={{ height: "16px", width: "16px" }}
												/>
											</IconButton>
										</div>
									</Box>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										label="View"
										variant="outlined"
										size="small"
										multiline
										maxRows={VIEW_NAME_MAX_ROWS}
										style={{ minWidth: "100%" }}
										inputProps={{
											...params.inputProps,
											title: viewSelected?.ViewName ?? "",
										}}
										sx={{
											"& .MuiInputBase-root": {
												height: `${VIEW_SELECTOR_HEIGHT}px`,
												alignItems: "center",
												paddingTop: 0,
												paddingBottom: 0,
											},
											"& .MuiInputBase-input": {
												lineHeight: 1.15,
												overflowWrap: "anywhere",
											},
										}}
									/>
								)}
							/>
						</Paper>
					</Grid2>
				</Grid2>

				<ManageTagsModalV2
					open={manageTagsOpen}
					handleClose={() => setManageTagsOpen(false)}
				/>

				<MesfModal
					open={discardChangesOpen}
					handleClose={handleCloseDiscardChanges}
					maxWidth="sm"
					title="UNSAVED CHANGES"
				>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Typography variant="body2">
							{viewSelected
								? `"${viewSelected.ViewName}" has changes that were never saved.`
								: "The current tags were never saved to a view."}{" "}
							{viewToLoad
								? `Loading "${viewToLoad.ViewName}" will discard them.`
								: "Creating a new view will discard them."}
						</Typography>
						<Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
							Are you sure you want to continue?
						</Typography>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<ConfirmCancelActionsV2
							confirmLabel="CONTINUE"
							cancelLabel="CANCEL"
							onConfirm={handleDiscardChanges}
							onCancel={handleCloseDiscardChanges}
						/>
					</MesfModal.Actions>
				</MesfModal>

				{/* View Management Modal (Create/Update/Delete) */}
				{viewModalMode && (
					<SaveUpdateDeleteViewModalV2
						open={true}
						mode={viewModalMode}
						view={viewForModal}
						handleClose={async (shouldUpdate: boolean) => {
							const deletedViewId = viewForModal?.ViewId ?? viewSelected?.ViewId;
							const deletedCurrentView =
								viewModalMode === "delete" &&
								shouldUpdate &&
								!!viewSelected &&
								deletedViewId === viewSelected.ViewId;

							setViewModalMode(null);
							setViewForModal(null);

							if (deletedCurrentView) {
								setViewSelected(null);
								setViewTagsAndRefetch({});
							}

							if (shouldUpdate) {
								await queryClient.invalidateQueries({ queryKey: ["views-v2"] });
							}
						}}
					/>
				)}

				{/* Error Modal */}
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		);
	},
);

HeaderSectionV2.displayName = "HeaderSectionV2";
